import React, { useState } from 'react';
import { Search, Download, Edit2, Trash2, Eye, Filter, MoreHorizontal, User, MapPin, Calendar, Award } from 'lucide-react';

const AVATAR_COLORS = ['bg-brand-blue', 'bg-teal-600', 'bg-amber-600'];

const TourGuidesManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [areaServed, setAreaServed] = useState('');
  const [languages, setLanguages] = useState('');
  const [licenseExpiry, setLicenseExpiry] = useState('Next 30 days');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const guides = [
    {
      id: 1,
      name: 'Anura Silva',
      nic: '198278990123',
      email: 'anura.silva@email.com',
      phone: '+94 77 123 4567',
      registration: 'TG-2019-0156',
      registeredDate: '2020-03-15',
      experience: '5 years',
      category: 'National Guide',
      areas: 'Colombo, Kandy, Galle',
      languages: 'English, Sinhala, Tamil',
      licenseExpiry: '2025-12-15',
      licenseStatus: 'Valid',
      status: 'Active',
      avatar: 'AS'
    },
    {
      id: 2,
      name: 'Priya Fernando',
      nic: '197334567890',
      email: 'priya.fernando@email.com',
      phone: '+94 71 987 6543',
      registration: 'TG-2021-0298',
      registeredDate: '2021-07-22',
      experience: '3 years',
      category: 'Regional Guide',
      areas: 'Nuwara Eliya, Ella',
      languages: 'English, Sinhala',
      licenseExpiry: '2025-03-22',
      licenseStatus: 'Expiring Soon',
      status: 'Active',
      avatar: 'PF'
    },
    {
      id: 3,
      name: 'Chaminda Perera',
      nic: '198612345678',
      email: 'chaminda.perera@email.com',
      phone: '+94 75 456 7890',
      registration: 'TG-2020-0187',
      registeredDate: '2020-11-08',
      experience: '4 years',
      category: 'Cultural Guide',
      areas: 'Anuradhapura, Polonnaruwa',
      languages: 'English, Sinhala, German',
      licenseExpiry: '2024-11-30',
      licenseStatus: 'Expired',
      status: 'Suspended',
      avatar: 'CP'
    }
  ];

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active': return 'bg-emerald-50 text-emerald-700';
      case 'suspended': return 'bg-red-50 text-red-700';
      case 'inactive': return 'bg-slate-100 text-slate-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getLicenseStatusColor = (status) => {
    switch (status.toLowerCase().replace(' ', '')) {
      case 'valid': return 'bg-emerald-50 text-emerald-700';
      case 'expiringsoon': return 'bg-amber-50 text-amber-700';
      case 'expired': return 'bg-red-50 text-red-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getCategoryColor = (category) => {
    switch (category.toLowerCase().replace(' ', '')) {
      case 'nationalguide': return 'bg-brand-blue/10 text-brand-blue';
      case 'regionalguide': return 'bg-teal-50 text-teal-700';
      case 'culturalguide': return 'bg-amber-50 text-amber-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tour Guide Management</h1>
          <div className="mt-1 text-sm text-slate-500">
            Home <span className="mx-1.5 text-slate-400">/</span>
            <span className="font-semibold text-slate-700">Tour Guides</span>
          </div>
        </div>
        <button
          onClick={() => window.location.href = '/TourGuideRegistration'}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white transition-colors rounded-lg shadow-sm bg-brand-blue hover:bg-brand-blue-dark"
        >
          <User size={16} />
          Add New Guide
        </button>
      </div>

      {/* Search and Filters Card */}
      <div className="bg-white border rounded-xl border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            <h2 className="text-sm font-bold text-slate-900">Search &amp; Filters</h2>
          </div>
        </div>

        <div className="p-6">
          {/* Main Search Row */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="relative flex-1 min-w-80">
              <Search className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, NIC, or registration number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-2.5 pl-10 pr-4 text-sm border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue transition-colors"
              />
            </div>

            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="px-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue bg-white min-w-40"
            >
              <option value="">All Categories</option>
              <option value="national">National Guide</option>
              <option value="regional">Regional Guide</option>
              <option value="cultural">Cultural Guide</option>
            </select>

            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-4 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue bg-white min-w-32"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="inactive">Inactive</option>
            </select>

            <button className="px-6 py-2.5 text-sm font-semibold text-white transition-colors rounded-lg bg-brand-blue hover:bg-brand-blue-dark">
              Search
            </button>

            <button className="px-4 py-2.5 text-sm font-semibold transition-colors bg-white border rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50">
              Reset
            </button>
          </div>

          {/* Advanced Filters Row */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col">
              <label className="mb-1 text-xs font-semibold text-slate-700">Area Served</label>
              <select
                value={areaServed}
                onChange={(e) => setAreaServed(e.target.value)}
                className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue bg-white min-w-36"
              >
                <option value="">All Areas</option>
                <option value="colombo">Colombo</option>
                <option value="kandy">Kandy</option>
                <option value="galle">Galle</option>
                <option value="anuradhapura">Anuradhapura</option>
              </select>
            </div>

            <div className="flex flex-col">
              <label className="mb-1 text-xs font-semibold text-slate-700">Languages</label>
              <select
                value={languages}
                onChange={(e) => setLanguages(e.target.value)}
                className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue bg-white min-w-36"
              >
                <option value="">All Languages</option>
                <option value="english">English</option>
                <option value="sinhala">Sinhala</option>
                <option value="tamil">Tamil</option>
                <option value="german">German</option>
              </select>
            </div>

            <div className="flex flex-col">
              <label className="mb-1 text-xs font-semibold text-slate-700">License Expiry</label>
              <select
                value={licenseExpiry}
                onChange={(e) => setLicenseExpiry(e.target.value)}
                className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue bg-white min-w-36"
              >
                <option value="Next 30 days">Next 30 days</option>
                <option value="Next 60 days">Next 60 days</option>
                <option value="Next 90 days">Next 90 days</option>
                <option value="All">All</option>
              </select>
            </div>

            <button className="flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors border rounded-lg bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100">
              <Download size={16} />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Results Card */}
      <div className="bg-white border rounded-xl border-slate-200">
        {/* Results Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500">Showing <span className="font-semibold text-slate-900">1-3</span> of <span className="font-semibold text-slate-900">87</span> tour guides</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Show</span>
            <select
              value={entriesPerPage}
              onChange={(e) => setEntriesPerPage(Number(e.target.value))}
              className="h-8 px-2 py-1 text-xs bg-white border rounded-md border-slate-200 focus:outline-none focus:border-brand-blue"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-xs text-slate-500">entries</span>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Guide Information</th>
                <th className="px-6 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Registration</th>
                <th className="px-6 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Category</th>
                <th className="px-6 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Service Areas</th>
                <th className="px-6 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">License Status</th>
                <th className="px-6 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Status</th>
                <th className="px-6 py-3 text-xs font-semibold text-left border-b text-slate-500 border-slate-200 bg-slate-50">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {guides.map((guide, index) => (
                <tr key={guide.id} className="transition-colors hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm text-white flex-shrink-0 ${AVATAR_COLORS[index % AVATAR_COLORS.length]}`}>
                        {guide.avatar}
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">{guide.name}</div>
                        <div className="text-xs text-slate-500">NIC: {guide.nic}</div>
                        <div className="text-xs text-slate-500">{guide.email}</div>
                        <div className="text-xs text-slate-500">{guide.phone}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="font-semibold text-slate-900">{guide.registration}</div>
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Calendar size={12} />
                        Registered: {guide.registeredDate}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Award size={12} />
                        Experience: {guide.experience}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getCategoryColor(guide.category)}`}>
                      {guide.category}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center gap-1 text-slate-600">
                        <MapPin size={12} className="flex-shrink-0 text-slate-400" />
                        {guide.areas}
                      </div>
                      <div className="text-slate-600">Languages: {guide.languages}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1.5">
                      <div className="text-xs text-slate-500">Expires: {guide.licenseExpiry}</div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getLicenseStatusColor(guide.licenseStatus)}`}>
                        {guide.licenseStatus}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(guide.status)}`}>
                      {guide.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5">
                      <button className="flex items-center justify-center transition-colors rounded-full w-7 h-7 bg-white border border-slate-200 text-slate-500 hover:bg-brand-blue/10 hover:text-brand-blue" title="View">
                        <Eye size={14} />
                      </button>
                      <button className="flex items-center justify-center transition-colors rounded-full w-7 h-7 bg-white border border-slate-200 text-slate-500 hover:bg-amber-50 hover:text-amber-600" title="Edit">
                        <Edit2 size={14} />
                      </button>
                      <button className="flex items-center justify-center transition-colors rounded-full w-7 h-7 bg-white border border-slate-200 text-slate-500 hover:bg-red-50 hover:text-red-600" title="Delete">
                        <Trash2 size={14} />
                      </button>
                      <button className="flex items-center justify-center transition-colors rounded-full w-7 h-7 bg-white border border-slate-200 text-slate-500 hover:bg-slate-100" title="More">
                        <MoreHorizontal size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
          <div className="text-xs text-slate-500">
            Showing <span className="font-semibold text-slate-900">1-3</span> of <span className="font-semibold text-slate-900">87</span> results
          </div>
          <div className="flex items-center space-x-1">
            <button className="flex items-center h-8 px-3 text-xs transition-colors border rounded-md border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed" disabled>
              Previous
            </button>
            <button className="flex items-center justify-center w-8 h-8 text-xs font-semibold text-white rounded-md bg-brand-blue">
              1
            </button>
            <button className="flex items-center justify-center w-8 h-8 text-xs font-medium transition-colors border rounded-md border-slate-200 text-slate-600 hover:bg-slate-50">
              2
            </button>
            <button className="flex items-center justify-center w-8 h-8 text-xs font-medium transition-colors border rounded-md border-slate-200 text-slate-600 hover:bg-slate-50">
              3
            </button>
            <span className="flex items-center justify-center w-8 h-8 text-xs text-slate-400">...</span>
            <button className="flex items-center justify-center w-8 h-8 text-xs font-medium transition-colors border rounded-md border-slate-200 text-slate-600 hover:bg-slate-50">
              29
            </button>
            <button className="flex items-center h-8 px-3 text-xs transition-colors border rounded-md border-slate-200 text-slate-500 hover:bg-slate-50">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourGuidesManagement;